import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack
} from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';

import FMSToolbar from 'src/common/FMSToolbar';
import FilterSelectInput from 'src/common/FilterSelectInput';
import TransactionListTable from 'src/components/transactions/TransactionListTable';
import { setTransactionType } from 'src/redux/transactionSlice';
import NewTransaction from 'src/components/transactions/NewTransaction';
import ReportFilterModal from 'src/components/reports/ReportFilterModal';
import DateFilterField from 'src/common/formFields/DateFilterField';

import {
  DATE_FORMAT,
  TRANSACTION_TYPES
} from 'src/utils/constants';

import {
  reportFormModel,
  reportFormInitialValues,
  reportValidationSchema
} from 'src/components/reports/reportFormModel';

import {
  inwardReportFormModel,
  inwardReportFormInitialValues,
  inwardReportValidationSchema
} from 'src/components/reports/inwardReportFormModel';

import {
  getFilterOptions,
  getReportDownloadURL,
  getReportFormTitle,
  getSearchPlaceholder
} from 'src/components/transactions/transactionService';
import useAuth from 'src/context/AuthContext';
import { hasAdminLikeAccess } from 'src/utils/getInitials';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';

const Transactions = ({ location }) => {
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const { transactionType } = useSelector((state) => state.transactions);

  const [openModal, setOpenModal] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterDate, setFilterDate] = useState(null);

  const isSales = transactionType !== TRANSACTION_TYPES[1].id;
  const isInward = transactionType === TRANSACTION_TYPES[1].id;
  const reportDownloadUrl = getReportDownloadURL(transactionType);
  const filterOptions = getFilterOptions(transactionType);
  const searchPlaceholder = getSearchPlaceholder(transactionType);

  const handleSearch = (value) => setSearchText(value);

  const handleGroupChange = (value) => {
    setFilterDate(null);
    dispatch(setTransactionType(value));
  };

  const filterByDate = (newValue) => {
    const dateString = newValue
      ? newValue.format(DATE_FORMAT.unixDate)
      : undefined;
    setFilterDate(dateString);
  };

  const clearDateFilter = () => setFilterDate(null);

  return (
    <>
      <Helmet>
        <title>Transactions | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* HEADER */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, mb: 0.5 }}
              >
                TRANSACTIONS
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Manage fuel inwards, issued,
                <br />
                and transfer records.
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <FMSToolbar
                showGroup
                placeholder={searchPlaceholder}
                actionName="Export"
                actionIcon={<DownloadIcon />}
                groupData={TRANSACTION_TYPES}
                groupType={transactionType}
                groupLabel="Category"
                onGroupChange={handleGroupChange}
                onSearch={handleSearch}
                onAction={() => setOpenReportModal(true)}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  '& .MuiTextField-root': {
                    flex: { xs: '1 1 auto', md: '0 0 auto' },
                    width: { xs: '100%', sm: 260, md: isInward ? 175 : 220 },
                  },
                  '& .MuiInputBase-root': {
                    height: 38
                  },
                  ...(isInward && {
                    '& .MuiCardContent-root > .MuiStack-root > .MuiStack-root:first-of-type': {
                      minWidth: 0
                    },
                    '& .MuiCardContent-root .MuiFormControl-root': {
                      width: { xs: '100%', sm: 180, md: 150 },
                      mr: 0
                    },
                    '& .MuiCardContent-root .MuiButton-root': {
                      minHeight: 38
                    }
                  })
                }}
              >
                <DateFilterField
                  value={filterDate}
                  onDateChange={filterByDate}
                  onClearFilter={clearDateFilter}
                  sx={{
                    width: { xs: '100%', sm: 180, md: isInward ? 160 : 160 }
                  }}
                />
                {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={setSiteFilter}
                  sx={{
                    width: { xs: '100%', sm: 220, md: isInward ? 150 : 180 },
                    mr: 0
                  }}
                />
                )}
                {isInward && hasAdminAccess && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minHeight: 38,
                    px: 2,
                    borderRadius: 2,
                  }}
                >
                  Add Transaction
                </Button>
                )}
              </FMSToolbar>
            </Stack>
          </Stack>

          {/* TOOLBAR (Everything inside same box) */}

          {/* TABLE */}
          <Box
            sx={{
              pt: 3,
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <TransactionListTable
              refreshData={location.state?.fromDashboard}
              searchText={searchText}
              dateFilter={filterDate}
              siteFilter={siteFilter}
              isSales={isSales}
            />
          </Box>

          {/* MODALS */}
          <NewTransaction
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <ReportFilterModal
            open={openReportModal}
            title={getReportFormTitle(transactionType)}
            formModel={isSales ? reportFormModel : inwardReportFormModel}
            formValidation={
            isSales ? reportValidationSchema : inwardReportValidationSchema
          }
            formInitValues={
            isSales
              ? reportFormInitialValues
              : inwardReportFormInitialValues
          }
            filterConfig={filterOptions}
            downloadUrl={reportDownloadUrl}
            handleClose={() => setOpenReportModal(false)}
          />

        </Container>
      </Box>
    </>
  );
};

Transactions.propTypes = {
  location: PropTypes.object
};

export default Transactions;
